import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthStore, useToolStore } from '@/store';
import { toolsApi } from '@/lib/api/tools';
import Text from '@/components/Typography/Text';
import StatusContainer from '@/components/StatusContainer';
import ToolGrid from './ToolGrid';

// Resolve saved IDs independently of the active catalog's search and pagination.
// Stop as soon as all available saved tools are found; never replace catalogSlugs.
export default function SavedTools({ onToolPress, refreshKey }) {
  const { t } = useTranslation();
  const favoriteIds = useAuthStore(s => s.user?.favoriteToolIds);
  const toolsBySlug = useToolStore(s => s.toolsBySlug);
  const upsertTools = useToolStore(s => s.upsertTools);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const idsKey = JSON.stringify((favoriteIds || []).map(String));
  const saved = useMemo(() => {
    const byId = new Map(Object.values(toolsBySlug).map(tool => [String(tool.id || tool._id), tool]));
    return JSON.parse(idsKey).map(id => byId.get(id)).filter(Boolean);
  }, [idsKey, toolsBySlug]);

  useEffect(() => {
    let cancelled = false;
    const ids = new Set(JSON.parse(idsKey));
    for (const tool of Object.values(useToolStore.getState().toolsBySlug)) ids.delete(String(tool.id || tool._id));
    setError(false);
    if (!ids.size) { setLoading(false); return; }
    setLoading(true);
    (async () => {
      let skip = 0;
      while (ids.size && !cancelled) {
        const data = await toolsApi.list({ limit: 200, skip });
        if (cancelled) return;
        const tools = data.tools || [];
        upsertTools(tools);
        for (const tool of tools) ids.delete(String(tool.id || tool._id));
        if (!data.pagination?.hasMore || !tools.length) break;
        skip += tools.length;
      }
    })().catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [idsKey, refreshKey, retry, upsertTools]);

  if (!favoriteIds?.length || (!loading && !error && !saved.length)) return null;
  return <View style={{ gap: 16, marginBottom: 36 }} testID="saved-tools-section">
    <Text tag="h2" weight="bold" style={{ fontSize: 20, lineHeight: 26, letterSpacing: -0.4 }}>{t('pages.tools.savedTools')}</Text>
    {saved.length > 0 && <ToolGrid tools={saved} onToolPress={onToolPress} />}
    {loading && !saved.length && <ToolGrid isLoading placeholderCount={Math.min(favoriteIds.length, 3)} />}
    {error && <StatusContainer type="error" message={t('pages.tools.savedToolsError')}
      actionText={t('pages.common.retry')} onAction={() => setRetry(value => value + 1)} />}
  </View>;
}
